import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "../utils/api";

const LogoInline = () => (
  <div style={{
    fontFamily: "'Anton', 'Archivo Black', Impact, sans-serif",
    display: "inline-flex",
    alignItems: "center",
    fontSize: "23.4px",
    letterSpacing: "-0.01em",
    lineHeight: 1,
    fontWeight: 400,
    textTransform: "uppercase",
  }}>
    <span style={{ color: "var(--paper)" }}>ROUGH</span>
    <span style={{
      display: "inline-block",
      width: "2px",
      height: "18px",
      background: "var(--orange)",
      margin: "0 7px",
      transform: "skewX(-20deg)",
    }} />
    <span style={{ color: "var(--orange)" }}>CUT</span>
  </div>
);

function NavLink({ to, children, onClick }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="nav-link"
    >
      {children}
    </Link>
  );
}

const Navbar = ({ user, setUser, onOpenAuth }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Close menu on route change
  useEffect(() => { setMenuOpen(false); }, [location]);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (!e.target.closest(".nav-header")) setMenuOpen(false);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [menuOpen]);

  const handleLogout = async () => {
    try { await api.post("/api/auth/logout/"); } catch {}
    setUser({ is_authenticated: false });
    setMenuOpen(false);
    window.location.reload();
  };

  const closeMenu = () => setMenuOpen(false);

  return (
    <header className="nav-header" style={{
      position: "sticky",
      top: 0,
      zIndex: 1000,
      background: "var(--ink)",
      borderBottom: "3px solid var(--orange)",
    }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 32px",
        maxWidth: 1440,
        margin: "0 auto",
      }}>
        <Link to="/" style={{ textDecoration: "none" }}>
          <LogoInline />
        </Link>

        {/* Desktop nav */}
        <nav className="nav-links-desktop" style={{ display: "flex", gap: 28, alignItems: "center" }}>
          <NavLink to="/discover">Discover</NavLink>
          <NavLink to="/interviews">Interviews</NavLink>
          <NavLink to="/about">About</NavLink>
          {user?.is_admin && (
            <>
              <NavLink to="/editor">New Post</NavLink>
              <NavLink to="/drafts">Drafts</NavLink>
              <NavLink to={`/authors/${user.username}`}>Author Page</NavLink>
            </>
          )}
          {user?.is_authenticated ? (
            <>
              <NavLink to="/profile">Profile</NavLink>
              <button className="btn" onClick={handleLogout}
                style={{ fontSize: 12, color: "var(--orange)" }}
                onMouseEnter={e => { e.currentTarget.style.color = "var(--ink)"; }}
                onMouseLeave={e => { e.currentTarget.style.color = "var(--orange)"; }}
              >
                Logout
              </button>
            </>
          ) : (
            <button className="btn btn-solid" onClick={() => onOpenAuth("signin")} style={{ fontSize: 12 }}>
              {"SIGN IN ↗︎"}
            </button>
          )}
        </nav>

        {/* Hamburger button — mobile only */}
        <button
          className="nav-hamburger"
          onClick={() => setMenuOpen(o => !o)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          style={{
            display: "none",
            background: "transparent",
            border: "2px solid var(--gray-dim)",
            color: "var(--paper)",
            padding: "6px 10px",
            cursor: "pointer",
            flexDirection: "column",
            gap: 5,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {menuOpen ? (
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          )}
        </button>
      </div>

      {/* Mobile drawer */}
      {menuOpen && (
        <nav className="nav-mobile-drawer">
          <NavLink to="/discover" onClick={closeMenu}>Discover</NavLink>
          <NavLink to="/interviews" onClick={closeMenu}>Interviews</NavLink>
          <NavLink to="/about" onClick={closeMenu}>About</NavLink>
          {user?.is_admin && (
            <>
              <NavLink to="/editor" onClick={closeMenu}>New Post</NavLink>
              <NavLink to="/drafts" onClick={closeMenu}>Drafts</NavLink>
              <NavLink to={`/authors/${user.username}`} onClick={closeMenu}>Author Page</NavLink>
            </>
          )}
          {user?.is_authenticated && (
            <NavLink to="/profile" onClick={closeMenu}>Profile</NavLink>
          )}
          <div style={{ marginTop: 8, paddingTop: 12 }}>
            {user?.is_authenticated ? (
              <button className="btn" onClick={handleLogout} style={{ fontSize: 12, color: "var(--orange)", width: "100%", justifyContent: "center" }}>
                Logout
              </button>
            ) : (
              <button className="btn btn-solid" onClick={() => { onOpenAuth("signin"); closeMenu(); }} style={{ fontSize: 12, width: "100%", justifyContent: "center" }}>
                {"SIGN IN ↗︎"}
              </button>
            )}
          </div>
        </nav>
      )}
    </header>
  );
};

export default Navbar;
